import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Button, Input, Tag, Spin, message } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined } from "@ant-design/icons";
import QuillHtmlDisplay from "components/QuillHtmlDisplay";
import PetitionDocumentPanel from "components/PetitionDocumentPanel";
import {
  formatPetitionDate,
  getPetitionDecision,
  getPetitionStatusMeta,
} from "utils/petitionHelpers";
import { submitPetitionDecision } from "../petitionWorkflowStats";

const DPAT_PRIMARY = "#16325a";

const PetitionReviewModal = ({
  open,
  row,
  reportingYear,
  canReview,
  user,
  onClose,
  onDecisionSaved,
}) => {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNotes("");
  }, [open, row?.districtId]);

  if (!row) return null;

  const meta = getPetitionStatusMeta(row.petitionStatus);
  const existingDecision = getPetitionDecision(row.committeeResponse);
  const isPending = row.petitionStatus === "pending";

  const handleDecision = async (decision) => {
    if (!canReview) return;
    if (!isPending && !window.confirm("This petition already has a decision. Replace it?")) {
      return;
    }

    try {
      setSaving(true);
      const saved = await submitPetitionDecision({
        districtId: row.districtId,
        year: reportingYear,
        decision,
        notes,
        committeeResponse: row.committeeResponse,
        user,
      });
      message.success(decision === "accepted" ? "Petition accepted" : "Petition rejected");
      onDecisionSaved?.({
        ...row,
        committeeResponse: saved,
        petitionStatus: decision,
      });
      onClose();
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to save decision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={
        <div>
          <div className="text-base font-semibold">{row.districtName}</div>
          <div className="text-xs font-normal text-muted-foreground">
            {row.regionName} · {reportingYear}
          </div>
        </div>
      }
      destroyOnClose
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Tag className={meta.badgeClass}>{meta.label}</Tag>
        {row.assessmentStatus && (
          <Tag color="default">Assessment: {row.assessmentStatus}</Tag>
        )}
      </div>

      <section className="mb-5 rounded-lg border border-border bg-muted/20 p-4">
        <h4 className="mb-2 text-sm font-semibold text-foreground">District petition</h4>
        {row.districtPetition?.comments ? (
          <>
            <QuillHtmlDisplay html={row.districtPetition.comments} />
            <p className="mt-2 text-right text-xs text-muted-foreground">
              Submitted {formatPetitionDate(row.petitionSubmittedAt)}
            </p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No petition text available.</p>
        )}
        <PetitionDocumentPanel
          districtId={row.districtId}
          year={reportingYear}
          districtName={row.districtName}
          petitionComment={row.districtPetition}
        />
      </section>

      {row.committeeResponse && (
        <section className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
          <h4 className="mb-2 text-sm font-semibold text-foreground">Committee response</h4>
          <QuillHtmlDisplay html={row.committeeResponse.comments} />
          {existingDecision && (
            <p className="mt-2 text-xs font-medium capitalize text-emerald-800">
              Decision: {existingDecision}
            </p>
          )}
        </section>
      )}

      <div className="mb-5 flex justify-center">
        <Link
          to={
            row.regionSlug && row.districtSlug
              ? `/ghana/${row.regionSlug}/${row.districtSlug}/dpat-assessment?year=${reportingYear}`
              : `/ghana/dpat-assessment?year=${reportingYear}`
          }
        >
          <Button
            type="primary"
            icon={<FileTextOutlined />}
            size="large"
            style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
          >
            View Assessment Sheet
          </Button>
        </Link>
      </div>

      {canReview && (isPending || existingDecision) && (
        <section className="rounded-lg border border-dashed border-border bg-background p-4">
          <h4 className="mb-2 text-sm font-semibold text-foreground">
            {isPending ? "Review decision" : "Update decision"}
          </h4>
          <Input.TextArea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes to include in the committee response…"
            disabled={saving}
          />
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              loading={saving}
              onClick={() => handleDecision("rejected")}
            >
              Reject petition
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={saving}
              onClick={() => handleDecision("accepted")}
              style={{ backgroundColor: DPAT_PRIMARY, borderColor: DPAT_PRIMARY }}
            >
              Accept petition
            </Button>
          </div>
        </section>
      )}

      {!canReview && saving && (
        <div className="flex justify-center py-4">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export default PetitionReviewModal;
